import { CouponCreateType, CouponCustomerInfoType } from "interfaces";
import { REGEX_VALID_EMAIL } from "utills/constants";
import { z } from "zod";

export const CreateCouponWithExistingConditionsSchema = () => {
  // Schema for coupons section
  const CouponsSchema = z.object({
    create_type: z.enum([CouponCreateType.MANUAL, CouponCreateType.GENERATE]),

    // For manual code
    code_info_list: z
      .array(
        z.object({
          code: z
            .string()
            .min(1, "Coupon code is required")
            .max(20, "Coupon code must be less than 20 characters")
            .regex(
              /^[a-zA-Z0-9_-]+$/,
              "Only letters, numbers, underscore and dash are allowed"
            ),
          customer_info_value: z.string().nullable(),
        })
      )
      .optional(),

    // For generate code - allow nullable
    generate_character_position: z
      .enum(["prefix", "suffix"])
      .nullable()
      .optional(),
    generate_characters: z
      .string()
      .min(2, "Enter from 2 to 18 characters")
      .max(18, "Enter from 2 to 18 characters")
      .regex(
        /^[a-zA-Z0-9_-]+$/,
        "Only letters, numbers, underscore and dash are allowed"
      )
      .nullable()
      .optional(),
    generate_character_length: z
      .number({
        invalid_type_error: "Please input a number",
      })
      .int()
      .min(2, "Enter from 2 to 18 characters")
      .max(18, "Enter from 2 to 18 characters")
      .nullable()
      .optional(),
    generate_total_coupons: z
      .number({
        invalid_type_error: "Please input a number",
      })
      .int()
      .min(1, "Must be greater than 0")
      .max(3000, "Enter number from 1 to 3000")
      .nullable()
      .optional(),

    // Max times used
    max_times_used: z
      .number({
        invalid_type_error: "Please input a number",
      })
      .int()
      .min(1, "Max times used must be at least 1"),

    is_active: z.boolean(),
    short_desc_vi: z.string().optional(),
    short_desc_en: z.string().optional(),
    displayed_on_actiup: z.boolean(),
    displayed_on_whitelabel: z.boolean(),

    // Customer info type
    customer_info_type: z
      .enum([CouponCustomerInfoType.CODE, CouponCustomerInfoType.EMAIL])
      .nullable()
      .optional(),
  });

  const AmountSchema = z.coerce
    .number({
      invalid_type_error: "Please input a number",
    })
    .nullable()
    .optional();

  return z
    .object({
      condition_id: z.string().min(1, "This field is required"),
      coupons: CouponsSchema.optional(),
    })
    .superRefine((data, ctx) => {
      // 1. Validate coupons section requirements
      if (!data.coupons) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Coupons section is required for Percentage and Fixed Amount discount types",
          path: ["coupons"],
        });
      } else {
        // Validate manual code requirements
        if (
          data.coupons.create_type === CouponCreateType.MANUAL &&
          (!data.coupons.code_info_list ||
            data.coupons.code_info_list.length === 0)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "At least one coupon code is required for manual type",
            path: ["coupons", "code_info_list"],
          });
        }

        // Validate generate code requirements
        if (data.coupons.create_type === CouponCreateType.GENERATE) {
          if (
            !data.coupons.generate_character_position ||
            !data.coupons.generate_characters ||
            data.coupons.generate_character_length === null ||
            data.coupons.generate_character_length === undefined ||
            data.coupons.generate_total_coupons === null ||
            data.coupons.generate_total_coupons === undefined
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "All generate code fields are required",
              path: ["coupons"],
            });
          } else {
            // Validate total characters
            const totalChars =
              data.coupons.generate_characters.length +
              data.coupons.generate_character_length;
            if (totalChars > 20) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message:
                  "Total characters (fixed text + random characters) cannot exceed 20",
                path: ["coupons", "generate_character_length"],
              });
            }

            // Validate character length limit
            const maxRandom = 20 - data.coupons.generate_characters.length;
            if (data.coupons.generate_character_length > maxRandom) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Invalid quantity number",
                path: ["coupons", "generate_character_length"],
              });
            }
          }
        }
      }

      // 2. Validate coupons code_info_list details
      if (data.coupons?.code_info_list) {
        const codes = data.coupons.code_info_list.map((item) => item.code);
        const duplicateIndexes = new Map();

        // Track duplicate codes
        if (data.coupons.create_type === "manual") {
          codes.forEach((code, index) => {
            if (!duplicateIndexes.has(code)) {
              duplicateIndexes.set(code, []);
            }
            duplicateIndexes.get(code).push(index);
          });
        }

        data.coupons.code_info_list.forEach((item, index) => {
          // Validate email format
          if (
            data.coupons?.customer_info_type === CouponCustomerInfoType.EMAIL &&
            item.customer_info_value &&
            !REGEX_VALID_EMAIL.test(item.customer_info_value)
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Invalid email format",
              path: ["coupons", "code_info_list", index, "customer_info_value"],
            });
          }

          // Validate customer code length
          if (
            data.coupons?.customer_info_type === CouponCustomerInfoType.CODE &&
            item.customer_info_value &&
            item.customer_info_value.length > 32
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: "Customer code must be less than 32 characters",
              path: ["coupons", "code_info_list", index, "customer_info_value"],
            });
          }

          // Validate unique coupon codes
          if (data.coupons?.create_type === CouponCreateType.MANUAL) {
            const duplicateCount = duplicateIndexes.get(item.code)?.length || 0;
            if (duplicateCount > 1) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Coupon code must be unique",
                path: ["coupons", "code_info_list", index, "code"],
              });
            }
          }
        });
      }
    });
};
